import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/shadcn/button';
import { Input } from 'components/shadcn/input';
import { Label } from 'components/shadcn/label';
import { ragApi } from '@/api/rag';
import { ChatMetadata } from '@/types';

type ChatConfig = Pick<ChatMetadata, 'lastMessageNeighbors' | 'conversationNeighbors'>;

export const RagSettings = ({ chatId }: { chatId: string }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ['chat-config', chatId],
    queryFn: () => ragApi.getChatConfig(chatId),
  });

  const { register, handleSubmit } = useForm<ChatConfig>({
    values: {
      lastMessageNeighbors: config?.lastMessageNeighbors || 0,
      conversationNeighbors: config?.conversationNeighbors || 0,
    },
  });

  const mutation = useMutation({
    mutationFn: (data: ChatConfig) => ragApi.updateChatConfig(chatId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-config', chatId] });
      toast.success(t('dashboard.toasts.saved'));
    },
    onError: () => toast.error(t('common.error')),
  });

  if (isLoading) return null;

  return (
    <div className="flex flex-col gap-4 border p-4 rounded-md bg-card">
      <h2 className="text-lg font-semibold text-balance">
        {t('dashboard.chatForm.ragSettings.title')}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <Label>{t('dashboard.chatForm.ragSettings.lastMessageNeighbors')}</Label>
          <Input type="number" {...register('lastMessageNeighbors', { valueAsNumber: true })} />
        </div>
        <div className="flex flex-col gap-2">
          <Label>{t('dashboard.chatForm.ragSettings.conversationNeighbors')}</Label>
          <Input type="number" {...register('conversationNeighbors', { valueAsNumber: true })} />
        </div>
      </div>
      <Button
        className="w-fit"
        size="sm"
        onClick={handleSubmit((data) => mutation.mutate(data))}
        disabled={mutation.isPending}
      >
        {t('dashboard.chatForm.ragSettings.save')}
      </Button>
    </div>
  );
};
